import { useMemo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Sparkles } from 'lucide-react'
import { BandReveal, HeroReveal, InteractiveDots, Pressable } from '@/components/motion'
import { DocThumb, DockMarquee, ScrollCards, StatsRow, type DocKind } from '@/components/landing/service'
import type { IndustryData, Template, Workflow } from '@/data/industries'
import { IndustryCtaBand } from './IndustryCtaBand'
import { IndustryListing, lowerFirst } from './listing'
import { stepAgent } from './WorkflowDiagram'

const DOC_KIND: Record<Template['type'], DocKind> = { Word: 'doc', Excel: 'sheet', PowerPoint: 'slide', PDF: 'doc' }
const ACCENTS = ['blue', 'purple', 'pink', 'green'] as const

/** A deliverable as it leaves the agents: the output, the agent's line, streaming text. */
function OutputTile({ workflow, industry }: { workflow: Workflow; industry: IndustryData }) {
  const agent = workflow.steps.map((s) => stepAgent(s, industry.aiAgents)).find(Boolean)
  return (
    <div className="flex h-full w-full flex-col gap-3 overflow-hidden bg-[var(--color-landing-primary)] p-4 text-white">
      <span className="flex items-center gap-1.5 text-[0.6rem] font-bold uppercase tracking-wide">
        <Sparkles aria-hidden className="h-3.5 w-3.5 text-[#ff00f2]" /> {agent?.name ?? 'Templatean AI'}
      </span>
      <span className="border border-white/25 p-2 text-[0.65rem] leading-snug">{agent?.catchphrase ?? workflow.name}</span>
      <span className="flex flex-col gap-1.5">
        {[100, 86, 94, 60].map((w, i) => (
          <span key={i} className="block h-[3px] bg-white/35" style={{ width: `${w}%` }} />
        ))}
      </span>
      <span className="mt-auto font-[family-name:var(--font-landing-display)] text-[0.7rem] uppercase leading-[1.35]">{workflow.output}</span>
    </div>
  )
}

/** Marquee tiles: the deliverable templates, with a finished output after every two. */
function deliverableTiles(industry: IndustryData): ReactNode[] {
  const tiles: ReactNode[] = []
  let w = 0
  industry.templates.forEach((t, i) => {
    tiles.push(<DocThumb key={t.name} kind={DOC_KIND[t.type]} title={t.name} accent={ACCENTS[i % ACCENTS.length]} className="h-full w-full" />)
    const workflow = industry.workflows[w]
    if (i % 2 === 1 && workflow) {
      tiles.push(<OutputTile key={workflow.name} workflow={workflow} industry={industry} />)
      w++
    }
  })
  return tiles
}

/**
 * Dock-marquee layout (Consulting; creative-content reference): white hero
 * with the name in a pink box, a 5rem H1, quote + button row and a
 * full-bleed dock-magnify marquee of the industry's deliverables; a pink
 * stats band; the pain points as blue scroll cards; the filterable listing
 * and a gradient CTA band.
 */
export function DockLayout({ industry }: { industry: IndustryData }) {
  const tiles = useMemo(() => deliverableTiles(industry), [industry])

  return (
    <>
      {/* 1. Hero: pink name box, H1, quote + button row, deliverables marquee */}
      <section className="overflow-hidden pt-16 md:pt-24 lg:pt-28">
        <div className="landing-container">
          <div className="lg:mx-auto lg:w-[80%]">
            <HeroReveal delay={0}>
              <p className="landing-text-display-xl">
                <span className="inline-block -rotate-1 bg-[#ff00f2] px-[0.25em] text-white">{industry.shortName}.</span>
              </p>
            </HeroReveal>
            <HeroReveal delay={0.1}>
              <h1 className="landing-text-hero mt-4 text-[var(--color-landing-gray-900)] dark:text-white md:mt-6">{industry.headline}</h1>
            </HeroReveal>
            <HeroReveal delay={0.2} className="mt-10 flex flex-col gap-8 md:mt-12 md:flex-row md:items-center md:gap-12">
              <p className="landing-text-body-lg max-w-[62ch] text-pretty border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)] md:text-xl">
                {industry.subheadline}
              </p>
              <Pressable className="inline-block shrink-0 self-start md:self-center">
                <Link to="/contact" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                  Book a demo
                  <ArrowRight aria-hidden className="h-5 w-5" />
                </Link>
              </Pressable>
            </HeroReveal>
          </div>
        </div>
        <HeroReveal delay={0.35} distance={40}>
          <DockMarquee className="mt-8 md:mt-12" tiles={tiles} />
        </HeroReveal>
      </section>

      {/* 2. Stats band: pink */}
      <BandReveal>
        <section aria-label={`${industry.shortName} in numbers`} className="landing-section-lg relative overflow-hidden bg-[#ff00f2]">
          <InteractiveDots />
          <div className="landing-container relative z-10">
            <div className="lg:mx-auto lg:w-[80%]">
              <StatsRow stats={industry.heroStats} size="display" />
            </div>
          </div>
        </section>
      </BandReveal>

      {/* 3. Pain points as blue scroll cards */}
      <ScrollCards
        heading="Where the hours go today."
        body={`Document intensity: ${lowerFirst(industry.documentIntensity)}. Here is where that volume slows ${industry.shortName.toLowerCase()} teams down, and what changes.`}
        cta={{ label: 'Book a demo', href: '/contact' }}
        documents={industry.templates.slice(0, 4).map((t) => ({ kind: DOC_KIND[t.type], title: t.name }))}
        cards={industry.painPoints.map((p) => ({
          title: p.title,
          description: `Today: ${lowerFirst(p.impact)}. With Templatean: ${lowerFirst(p.solution)}.`,
          icon: p.icon,
        }))}
      />

      {/* 4. Listing with filters */}
      <IndustryListing industry={industry} />

      {/* 5. CTA band: gradient */}
      <IndustryCtaBand industry={industry} tone="gradient" />
    </>
  )
}
